/**
 * Identification — the adjudication section (stage `subjects_identified`).
 *
 * New presentation over the existing endpoints (select-candidate /
 * clarify / manual-resolve): per subject an AdjudicationRow with four
 * states — matched (auto or picked; review expander with a field-level
 * compare), pick-one (candidate pick-list, each expandable to a
 * side-by-side FieldCompare), ai-working (graph in flight), none (zero
 * candidates → clarify / create manually).
 *
 * "Dismiss" narrows the pick-list client-side only — there is no
 * dismiss endpoint; the decision of record is the eventual Confirm.
 * Polls /reports/{id}/candidates every 3s while any subject is in
 * flight (same cadence the gate strip uses for its ai_working chip).
 */

import { useEffect, useRef, useState } from 'react';

import {
  Badge,
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { RelatedEntitiesPanel } from '@/components/RelatedEntities/RelatedEntitiesPanel';
import { isVesselSubjectType, jurisdictionFieldLabel, SUBJECT_TYPE_OPTIONS } from '@/constants';
import type { useReportRequestTriage } from '@/hooks';
import { getCachedCandidates, setCachedCandidates } from '@/hooks/subjectCandidatesCache';
import { UNCHANGED, usePoll, withEtag } from '@/hooks/usePoll';
import { vnextApiClient } from '@/services/vnext-api';
import { TriageManualResolveRequest } from '@/types/report-request';
import { ApiSubject } from '@/types/screening';
import {
  Confidence,
  SubjectCandidateResponse,
  SubjectWithCandidates,
} from '@/types/subject-candidate';
import { toError } from '@/utils/errors';

const POLL_INTERVAL_MS = 3000;

const CONFIDENCE_COLOR: Record<Confidence, string> = {
  high: 'green',
  medium: 'yellow',
  low: 'red',
};

interface IdentificationSectionProps {
  reportRequestId: string;
  reportId: string | null;
  /** True when the request is past SUBJECTS_IDENTIFIED — the backend
   * rejects adjudication then, so the verbs are hidden and the section
   * reads as a record of what was matched. */
  readOnly?: boolean;
  triageActions: Pick<
    ReturnType<typeof useReportRequestTriage>,
    'selectCandidate' | 'clarifySubject' | 'resolveSubjectManually'
  >;
  /** Re-read the stepper's gates. Firing entity research holds Start until it
   *  lands, so a decision here can change what the gate strip shows. */
  onGatesChanged?: () => void;
}

export function IdentificationSection({
  reportRequestId,
  reportId,
  readOnly = false,
  triageActions,
  onGatesChanged,
}: Readonly<IdentificationSectionProps>) {
  const [subjects, setSubjects] = useState<Map<string, ApiSubject>>(new Map());
  // Subjects awaiting a fresh round after a clarify: subject_id → the
  // iteration observed at submit. Keeps polling until a newer round lands.
  const [clarifying, setClarifying] = useState<Record<string, number>>({});
  const clarifyingRef = useRef(clarifying);
  clarifyingRef.current = clarifying;

  const candidates = usePoll<SubjectWithCandidates[]>({
    enabled: reportId !== null,
    refetchDeps: [reportId],
    // Poll while any subject is still being identified (no candidates
    // yet) or awaiting a fresh round after a clarify.
    interval: (latest) =>
      (latest ?? []).some((r) => r.candidates.length === 0) ||
      Object.keys(clarifyingRef.current).length > 0
        ? POLL_INTERVAL_MS
        : null,
    queryFn: async ({ signal, etag, manual }) => {
      if (!reportId) {
        throw new Error('reportId is required');
      }
      const result = await vnextApiClient.getReportCandidates(reportId, {
        signal,
        etag: manual ? null : etag,
      });
      if (result === 'not-modified') {
        return UNCHANGED;
      }
      setCachedCandidates(reportId, result.data);
      return withEtag(result.data, result.etag);
    },
    onData: (result) => {
      setClarifying((prev) => {
        if (Object.keys(prev).length === 0) {
          return prev;
        }
        const next = { ...prev };
        for (const r of result) {
          const stamp = next[r.subject_id];
          if (
            stamp !== undefined &&
            (r.is_resolved || (r.iteration > stamp && r.candidates.length > 0))
          ) {
            delete next[r.subject_id];
          }
        }
        return next;
      });
    },
    onError: (err) => Sentry.captureException(toError(err, 'Failed to load candidates')),
  });
  // Seed from the process-lifetime cache so a re-mount paints instantly.
  const rows = candidates.data ?? getCachedCandidates(reportId) ?? null;
  const error =
    candidates.error === undefined
      ? null
      : toError(candidates.error, 'Failed to load candidates').message;
  const { refetch, setData: setRows } = candidates;

  // Related entities are attributed to a candidate, so they only become
  // adjudicable once one has been picked.
  const anyResolved = (rows ?? []).some((r) => r.is_resolved);

  // Subject rows carry the extracted fields the compare view diffs against.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await vnextApiClient.listTriageSubjects(reportRequestId);
        if (!cancelled) {
          setSubjects(new Map(list.map((s) => [s.id, s])));
        }
      } catch {
        // Compare rows degrade to candidate-only display; not fatal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportRequestId]);

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        {readOnly
          ? 'The identification decisions for this request. Use "Change" on this section to revert and re-adjudicate.'
          : 'Identification fires per subject; single confident matches auto-resolve. Compare the remaining candidates field-by-field, confirm the right one — or clarify with more context, or record the subject manually.'}
      </Text>

      {!rows && <Loader />}
      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}
      {rows && rows.length === 0 && (
        <Text size="sm" c="dimmed">
          No subjects on this report.
        </Text>
      )}

      {rows?.map((row) => (
        <AdjudicationRow
          key={row.subject_id}
          row={row}
          readOnly={readOnly}
          subject={subjects.get(row.subject_id) ?? null}
          onConfirm={async (candidateId) => {
            await triageActions.selectCandidate(row.subject_id, candidateId);
            await refetch();
          }}
          onClarify={async (ctx) => {
            await triageActions.clarifySubject(row.subject_id, ctx);
            setClarifying((prev) => ({ ...prev, [row.subject_id]: row.iteration }));
            setRows((prev) =>
              (prev ?? rows ?? []).map((r) =>
                r.subject_id === row.subject_id ? { ...r, candidates: [] } : r
              )
            );
            await refetch();
          }}
          onResolveManually={async (payload) => {
            await triageActions.resolveSubjectManually(row.subject_id, payload);
            await refetch();
          }}
        />
      ))}

      {/* Entities identification found around the selected candidate. Only
          meaningful once something is resolved — before that they are still
          candidate-scoped and the backend returns nothing. Subordinate to the
          adjudication above: picking the right subject is still the main job
          on this screen. */}
      {anyResolved && (
        <RelatedEntitiesPanel reportRequestId={reportRequestId} onChanged={onGatesChanged} />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// AdjudicationRow — one subject's adjudication state + verbs
// ---------------------------------------------------------------------------

interface AdjudicationRowProps {
  row: SubjectWithCandidates;
  readOnly?: boolean;
  subject: ApiSubject | null;
  onConfirm: (candidateId: string) => Promise<void>;
  onClarify: (additionalContext: string) => Promise<void>;
  onResolveManually: (payload: TriageManualResolveRequest) => Promise<void>;
}

function AdjudicationRow({
  row,
  readOnly = false,
  subject,
  onConfirm,
  onClarify,
  onResolveManually,
}: Readonly<AdjudicationRowProps>) {
  const selected = row.candidates.find((c) => c.is_selected) ?? null;
  const working = !row.is_resolved && row.candidates.length === 0;
  const pickOne = !row.is_resolved && row.candidates.length > 0;
  // Client-side narrowing only — no dismiss endpoint exists.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [reviewOpen, setReviewOpen] = useState(false);

  const visibleCandidates = row.candidates.filter((c) => !dismissed.has(c.id));
  const hiddenCount = row.candidates.length - visibleCandidates.length;

  return (
    <Paper withBorder p="md" radius="md" data-testid={`adjudication-${row.subject_id}`}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Text fw={600}>{row.subject_name}</Text>
            {selected && (
              <Badge color="green" variant="filled" leftSection={<IconCheck size={12} />}>
                Matched: {selected.name}
              </Badge>
            )}
            {working && (
              <Badge color="blue" variant="light" leftSection={<Loader size={10} />}>
                Identifying…
              </Badge>
            )}
            {pickOne && (
              <Badge color="yellow" variant="light">
                {visibleCandidates.length} candidate{visibleCandidates.length === 1 ? '' : 's'} —
                pick one
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              round {row.iteration}
            </Text>
          </Group>
          {selected && (
            <Button
              size="compact-xs"
              variant="subtle"
              rightSection={
                reviewOpen ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />
              }
              onClick={() => setReviewOpen((v) => !v)}
              data-testid={`review-match-${row.subject_id}`}
            >
              Review match
            </Button>
          )}
        </Group>

        {working && (
          <Text size="sm" c="dimmed">
            The identification graph is running — safe to leave this page; results land here and in
            your inbox.
          </Text>
        )}

        {selected && (
          <Collapse in={reviewOpen}>
            <FieldCompare subject={subject} candidate={selected} subjectName={row.subject_name} />
          </Collapse>
        )}

        {pickOne && (
          <Stack gap="xs">
            {visibleCandidates.map((c) => (
              <CandidateCompareRow
                key={c.id}
                candidate={c}
                readOnly={readOnly}
                subject={subject}
                subjectName={row.subject_name}
                onConfirm={() => onConfirm(c.id)}
                onDismiss={() => setDismissed((prev) => new Set(prev).add(c.id))}
              />
            ))}
            {hiddenCount > 0 && (
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {hiddenCount} dismissed (this view only)
                </Text>
                <Button size="compact-xs" variant="subtle" onClick={() => setDismissed(new Set())}>
                  Restore
                </Button>
              </Group>
            )}
            {visibleCandidates.length > 1 && (
              <Group>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setDismissed(new Set(row.candidates.map((c) => c.id).slice(1)))}
                >
                  Dismiss all but first
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {!row.is_resolved && !readOnly && (
          <Group gap="xs" wrap="wrap">
            <ClarifyAction subjectName={row.subject_name} onSubmit={onClarify} />
            <ManualResolveAction
              subjectName={row.subject_name}
              emphasised={!working && row.candidates.length === 0}
              onSubmit={onResolveManually}
            />
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Candidate row with expandable side-by-side compare + Confirm/Dismiss
// ---------------------------------------------------------------------------

function CandidateCompareRow({
  candidate,
  readOnly = false,
  subject,
  subjectName,
  onConfirm,
  onDismiss,
}: Readonly<{
  candidate: SubjectCandidateResponse;
  readOnly?: boolean;
  subject: ApiSubject | null;
  subjectName: string;
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
}>) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Paper p="sm" withBorder radius="sm" shadow="none">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" style={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <Text fw={500}>{candidate.name}</Text>
            <Badge color={CONFIDENCE_COLOR[candidate.confidence]} size="sm" variant="light">
              {candidate.confidence}
            </Badge>
          </Group>
          {!readOnly && (
            <Group gap="xs" wrap="nowrap">
              <Button
                size="xs"
                onClick={() => void handleConfirm()}
                loading={confirming}
                data-testid={`confirm-candidate-${candidate.id}`}
              >
                Confirm
              </Button>
              <Button size="xs" variant="subtle" color="gray" onClick={onDismiss}>
                Dismiss
              </Button>
            </Group>
          )}
        </Group>
        {(candidate.aliases?.length ?? 0) > 0 && (
          <Text size="xs" c="dimmed">
            Aliases: {candidate.aliases.join(', ')}
          </Text>
        )}
        <Collapse in={open}>
          <FieldCompare subject={subject} candidate={candidate} subjectName={subjectName} />
        </Collapse>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// FieldCompare — the requested subject vs a candidate, field by field
// ---------------------------------------------------------------------------

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') {
    return '—';
  }
  if (Array.isArray(v)) {
    return v.length ? v.map((x) => formatValue(x)).join(', ') : '—';
  }
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function FieldCompare({
  subject,
  candidate,
  subjectName,
}: Readonly<{
  subject: ApiSubject | null;
  candidate: SubjectCandidateResponse;
  subjectName: string;
}>) {
  const attrs = candidate.attributes ?? {};
  const subjectInfo = (subject?.additional_info ?? {}) as Record<string, unknown>;

  const rows: { field: string; requested: string; candidate: string }[] = [
    { field: 'Name', requested: subject?.subject_name ?? subjectName, candidate: candidate.name },
    {
      field: 'Type',
      requested: formatValue(subject?.subject_type),
      candidate: formatValue(attrs.subject_type ?? attrs.type),
    },
    {
      field: 'Jurisdictions',
      requested: formatValue((subject?.jurisdictions ?? []).map((j) => j.name ?? j.alpha2code)),
      candidate: formatValue(attrs.jurisdictions ?? attrs.jurisdiction ?? attrs.nationality),
    },
    { field: 'Reference', requested: formatValue(subject?.reference), candidate: '—' },
  ];

  // Fold in the candidate's remaining attributes against the subject's
  // additional_info twin, so DOB-ish / registration fields line up.
  const shown = new Set(['subject_type', 'type', 'jurisdictions', 'jurisdiction', 'nationality']);
  for (const [key, value] of Object.entries(attrs)) {
    if (shown.has(key) || value === null || value === undefined || value === '') {
      continue;
    }
    rows.push({
      field: key.replace(/_/g, ' '),
      requested: formatValue(subjectInfo[key]),
      candidate: formatValue(value),
    });
  }

  const matches = (a: string, b: string) =>
    a !== '—' && b !== '—' && a.trim().toLowerCase() === b.trim().toLowerCase();

  return (
    <Table withTableBorder={false} verticalSpacing={4} horizontalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={140}>
            <Text size="xs" c="dimmed">
              Field
            </Text>
          </Table.Th>
          <Table.Th>
            <Text size="xs" c="dimmed">
              Requested
            </Text>
          </Table.Th>
          <Table.Th>
            <Text size="xs" c="dimmed">
              Candidate
            </Text>
          </Table.Th>
          <Table.Th w={30} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((r) => (
          <Table.Tr key={r.field}>
            <Table.Td>
              <Text size="xs" fw={500}>
                {r.field}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs">{r.requested}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs">{r.candidate}</Text>
            </Table.Td>
            <Table.Td>
              {matches(r.requested, r.candidate) ? (
                <Text size="xs" c="green">
                  ✓
                </Text>
              ) : r.requested !== '—' && r.candidate !== '—' ? (
                <Text size="xs" c="orange">
                  ⚠
                </Text>
              ) : null}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Clarify + manual-resolve modals (ported from the retired
// TriageIdentificationPanel — same endpoints, same fields)
// ---------------------------------------------------------------------------

function ClarifyAction({
  subjectName,
  onSubmit,
}: Readonly<{ subjectName: string; onSubmit: (additionalContext: string) => Promise<void> }>) {
  const [opened, { open, close }] = useDisclosure(false);
  const [context, setContext] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!context.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(context.trim());
      setContext('');
      close();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="subtle" size="xs" onClick={open}>
        None of these match — clarify
      </Button>
      <Modal opened={opened} onClose={close} title={`Clarify ${subjectName}`}>
        <Stack gap="sm">
          <Text size="sm">
            Add context the LLM should consider on the next pass — e.g. industry, location,
            distinguishing details. Identification re-runs with the existing inputs plus this text.
          </Text>
          <Textarea
            placeholder="e.g. they're a Singapore-based logistics company, not the Korean conglomerate"
            minRows={4}
            autosize
            value={context}
            onChange={(e) => setContext(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting} disabled={!context.trim()}>
              Re-run identification
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * Escape hatch for subjects the identification graph can't find (e.g. a
 * brand-new company that isn't online yet). Name + type required.
 */
function ManualResolveAction({
  subjectName,
  emphasised,
  onSubmit,
}: Readonly<{
  subjectName: string;
  emphasised: boolean;
  onSubmit: (payload: TriageManualResolveRequest) => Promise<void>;
}>) {
  const [opened, { open, close }] = useDisclosure(false);
  const [name, setName] = useState(subjectName);
  const [type, setType] = useState<string | null>(null);
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [reference, setReference] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [imoNumber, setImoNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [addresses, setAddresses] = useState<string[]>([]);
  const [urls, setUrls] = useState<string[]>([]);
  const [aliases, setAliases] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim().length > 0 && !!type;

  const handleOpen = () => {
    setName(subjectName);
    open();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const additionalInfo: Record<string, unknown> = {};
    if (registrationNumber.trim()) {
      additionalInfo.registration_number = registrationNumber.trim();
    }
    if (isVesselSubjectType(type) && imoNumber.trim()) {
      additionalInfo.imo_number = imoNumber.trim();
    }
    if (nationality.trim()) {
      additionalInfo.nationality = nationality.trim();
    }
    if (addresses.length) {
      additionalInfo.addresses = addresses;
    }
    if (urls.length) {
      additionalInfo.urls = urls;
    }
    if (aliases.length) {
      additionalInfo.aliases = aliases;
    }
    if (notes.trim()) {
      additionalInfo.uncategorised = { notes: notes.trim() };
    }

    const codes = jurisdictions.map((c) => c.trim().toUpperCase()).filter(Boolean);

    const payload: TriageManualResolveRequest = {
      subject_name: name.trim(),
      subject_type: type as string,
      ...(codes.length ? { jurisdiction_codes: codes } : {}),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(Object.keys(additionalInfo).length ? { additional_info: additionalInfo } : {}),
    };

    setSubmitting(true);
    try {
      await onSubmit(payload);
      close();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant={emphasised ? 'light' : 'subtle'} size="xs" onClick={handleOpen}>
        Create subject manually
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={`Create subject manually — ${subjectName}`}
        size="lg"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Record this subject from information you have to hand. Identification won&apos;t run —
            the subject is marked resolved and triage continues. Name and type are required; add
            whatever else you have.
          </Text>
          <TextInput
            label="Subject name"
            withAsterisk
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Select
            label="Subject type"
            withAsterisk
            placeholder="Company, Person or Vessel"
            data={SUBJECT_TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
          {isVesselSubjectType(type) && (
            <TextInput
              label="IMO number"
              description="Seven digits — the vessel's identity through renames and re-flagging."
              placeholder="e.g. 9116412"
              value={imoNumber}
              onChange={(e) => setImoNumber(e.currentTarget.value)}
            />
          )}
          <TagsInput
            label={jurisdictionFieldLabel(type)}
            description={
              isVesselSubjectType(type)
                ? 'Optional — the flag state as ISO alpha-2 (e.g. PA). The report also shows the flag as listed.'
                : 'Recommended — ISO alpha-2 (e.g. GQ). Improves downstream screening accuracy.'
            }
            placeholder="Type a code and press Enter"
            value={jurisdictions}
            onChange={setJurisdictions}
          />
          <TextInput
            label="Reference"
            placeholder="Optional client / internal reference"
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
          />
          <Group grow>
            <TextInput
              label="Registration number"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.currentTarget.value)}
            />
            <TextInput
              label="Nationality"
              value={nationality}
              onChange={(e) => setNationality(e.currentTarget.value)}
            />
          </Group>
          <TagsInput label="Aliases" value={aliases} onChange={setAliases} />
          <TagsInput label="Addresses" value={addresses} onChange={setAddresses} />
          <TagsInput label="URLs" value={urls} onChange={setUrls} />
          <Textarea
            label="Other notes"
            description="Anything that doesn't fit above (e.g. details from a physical document)."
            minRows={2}
            autosize
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
              Create &amp; resolve
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
